// frontend/src/pages/ProductsPage.js
import React, { useState, useMemo, useEffect } from 'react';
import { 
    Container, 
    Grid, 
    Typography, 
    TextField, 
    MenuItem, 
    Box, 
    Pagination,
    Paper,
    InputAdornment,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useProducts } from '../context/ProductContext';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';

function ProductsPage() {
    const { products, loading: contextLoading } = useProducts();
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('-created_at');
    const [page, setPage] = useState(1);
    const itemsPerPage = 12;

    // ✅ Get unique categories for filter
    const categories = useMemo(() => {
        if (!products || products.length === 0) return [];
        const uniqueCategories = [...new Set(products.map(p => p.category || 'Uncategorized'))];
        return uniqueCategories;
    }, [products]);

    // ✅ Filter and sort products
    const filteredAndSortedProducts = useMemo(() => {
        if (!products || products.length === 0) return [];

        let filtered = [...products];

        // Filter by search
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(product => 
                product.title?.toLowerCase().includes(searchLower) ||
                product.description?.toLowerCase().includes(searchLower) ||
                product.category?.toLowerCase().includes(searchLower) ||
                product.brand?.toLowerCase().includes(searchLower)
            );
        }

        // Sort
        switch (sortBy) {
            case '-created_at':
                filtered.sort((a, b) => (b.id || 0) - (a.id || 0));
                break;
            case 'created_at':
                filtered.sort((a, b) => (a.id || 0) - (b.id || 0));
                break;
            case '-price':
                filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case 'price':
                filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case 'title':
                filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
                break;
            case '-title':
                filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
                break;
            case '-rating':
                filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                break;
            default:
                break;
        }

        return filtered;
    }, [products, search, sortBy]);

    // ✅ Paginate products
    const paginatedProducts = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredAndSortedProducts.slice(startIndex, endIndex);
    }, [filteredAndSortedProducts, page]);

    const totalPages = Math.ceil(filteredAndSortedProducts.length / itemsPerPage);

    // Reset page when search or sort changes
    useEffect(() => {
        setPage(1);
    }, [search, sortBy]);

    if (contextLoading) {
        return <LoadingSpinner />;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                All Products
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {products.length} products available
            </Typography>

            {/* Search and Filter */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Search Products"
                        variant="outlined"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1, minWidth: 200 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        label="Sort By"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="-created_at">Newest First</MenuItem>
                        <MenuItem value="created_at">Oldest First</MenuItem>
                        <MenuItem value="-price">Price: High to Low</MenuItem>
                        <MenuItem value="price">Price: Low to High</MenuItem>
                        <MenuItem value="-rating">Highest Rated</MenuItem>
                        <MenuItem value="title">Name A-Z</MenuItem>
                        <MenuItem value="-title">Name Z-A</MenuItem>
                    </TextField>
                </Box>

                {/* Category Filters */}
                {categories.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                            label="All" 
                            size="small"
                            variant="filled"
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                        />
                        {categories.slice(0, 8).map((cat) => (
                            <Chip 
                                key={cat} 
                                label={cat} 
                                size="small"
                                variant="outlined"
                                onClick={() => setSearch(cat)}
                                sx={{ cursor: 'pointer' }}
                            />
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {paginatedProducts.length} of {filteredAndSortedProducts.length} products
            </Typography>

            {/* Products Grid */}
            {paginatedProducts.length > 0 ? (
                <>
                    <Grid container spacing={2.5}>
                        {paginatedProducts.map((product) => (
                            <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
                                <ProductCard product={product} />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </>
            ) : (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="h6" color="text.secondary">
                        No products found matching your criteria
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Try adjusting your search or filters
                    </Typography>
                </Paper>
            )}
        </Container>
    );
}

export default ProductsPage;